/**
 * Serviço de sincronização com Google Calendar
 *
 * Sincroniza tarefas com hora definida (data_inicio_tem_hora === true)
 * onde o usuário logado é responsável.
 */

import { supabase } from '@features/sistema/services/supabaseClient'

// Tipos
interface Tarefa {
  id: string
  nome: string
  descricao: string | null
  data_inicio: string | null
  data_inicio_tem_hora?: boolean
  data_conclusao: string | null
  status: string
  prioridade: string
  obra_id: string
  obra?: { nome: string }
}

interface CalendarEvent {
  summary: string
  description?: string
  start: {
    dateTime: string
    timeZone: string
  }
  end: {
    dateTime: string
    timeZone: string
  }
  extendedProperties?: {
    private: {
      acor_tarefa_id: string
      acor_obra_id: string
    }
  }
}

interface SyncResult {
  success: boolean
  count: number
  errors: string[]
  syncedTasks: string[]
}

/** Normaliza obra vinda do Supabase (relação retorna array) para Tarefa. */
function toTarefa(row: { obra?: { nome: string } | { nome: string }[]; [k: string]: unknown }): Tarefa {
  const obra = Array.isArray(row.obra) ? row.obra[0] : row.obra
  return { ...row, obra } as Tarefa
}

// Constantes
const GOOGLE_CALENDAR_API = 'https://www.googleapis.com/calendar/v3'
const TIME_ZONE = 'America/Sao_Paulo'
const LOCAL_STORAGE_KEY = 'acor-google-calendar-sync'
const GOOGLE_TOKEN_KEY = 'acor-google-calendar-token'

interface StoredGoogleTokens {
  accessToken: string
  refreshToken?: string
  savedAt: string
  expiresAt?: string // ISO date quando o access token expira
}

/**
 * Salva os tokens do Google no localStorage
 */
export function saveGoogleToken(accessToken: string, refreshToken?: string): void {
  try {
    // Manter refresh token existente se não for passado um novo
    const existing = getStoredGoogleTokens()
    const data: StoredGoogleTokens = {
      accessToken,
      refreshToken: refreshToken || existing?.refreshToken,
      savedAt: new Date().toISOString(),
      // Access tokens do Google expiram em ~1 hora
      expiresAt: new Date(Date.now() + 55 * 60 * 1000).toISOString()
    }
    localStorage.setItem(GOOGLE_TOKEN_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Erro ao salvar token do Google:', error)
  }
}

/**
 * Obtém os tokens armazenados (access + refresh)
 */
function getStoredGoogleTokens(): StoredGoogleTokens | null {
  try {
    const data = localStorage.getItem(GOOGLE_TOKEN_KEY)
    if (data) {
      const parsed = JSON.parse(data)
      // Compatibilidade com formato antigo { token: string }
      if (parsed.token && !parsed.accessToken) {
        return { accessToken: parsed.token, savedAt: parsed.savedAt || '' }
      }
      return parsed
    }
  } catch (error) {
    console.error('Erro ao ler tokens do Google:', error)
  }
  return null
}

/**
 * Obtém o token do Google do localStorage
 */
export function getStoredGoogleToken(): string | null {
  return getStoredGoogleTokens()?.accessToken || null
}

/**
 * Verifica se o access token está provavelmente expirado
 */
function isTokenExpired(): boolean {
  const tokens = getStoredGoogleTokens()
  if (!tokens?.expiresAt) return true
  return new Date() >= new Date(tokens.expiresAt)
}

/**
 * Remove o token do Google do localStorage
 */
export function clearGoogleToken(): void {
  localStorage.removeItem(GOOGLE_TOKEN_KEY)
}

/**
 * Tenta renovar o access token usando o refresh token via Edge Function do Supabase
 * Retorna o novo access token ou null se falhar
 */
async function refreshGoogleAccessToken(): Promise<string | null> {
  const tokens = getStoredGoogleTokens()
  if (!tokens?.refreshToken) {
    console.warn('⚠️ Sem refresh token do Google armazenado — necessário reconectar')
    return null
  }

  try {
    console.log('🔄 Tentando renovar access token do Google via Edge Function...')
    const { data, error } = await supabase.functions.invoke('refresh-google-token', {
      body: { refresh_token: tokens.refreshToken }
    })

    if (error || !data?.access_token) {
      console.error('❌ Erro ao renovar token do Google:', error || data)
      return null
    }

    // Salvar novo access token (mantendo o refresh token)
    saveGoogleToken(data.access_token, tokens.refreshToken)
    console.log('✅ Access token do Google renovado com sucesso')
    return data.access_token
  } catch (error) {
    console.error('❌ Erro ao chamar Edge Function de refresh:', error)
    return null
  }
}

/**
 * Faz uma chamada à API do Google Calendar com retry automático se token expirar
 */
async function fetchWithTokenRefresh(url: string, options: RequestInit = {}): Promise<Response> {
  let token = await getGoogleAccessToken()
  if (!token) throw new Error('Sem token do Google')

  const headers = { ...options.headers as Record<string, string>, 'Authorization': `Bearer ${token}` }
  let response = await fetch(url, { ...options, headers })

  // Se o token expirou, tentar renovar e repetir a chamada
  if (response.status === 401) {
    console.log('🔄 Token expirado, tentando renovar...')
    const newToken = await refreshGoogleAccessToken()
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`
      response = await fetch(url, { ...options, headers })
    }
  }

  return response
}

/**
 * Verifica se o usuário tem token do Google Calendar
 * Retorna um objeto com status detalhado
 */
export async function isGoogleCalendarConnected(): Promise<boolean> {
  const result = await getGoogleCalendarConnectionStatus()
  return result.hasToken && result.apiEnabled
}

/**
 * Status detalhado da conexão com Google Calendar
 */
export interface CalendarConnectionStatus {
  hasToken: boolean
  apiEnabled: boolean
  error?: string
}

/**
 * Verifica status detalhado da conexão com Google Calendar
 */
export async function getGoogleCalendarConnectionStatus(): Promise<CalendarConnectionStatus> {
  try {
    // Primeiro, verificar se tem token armazenado localmente
    const storedToken = getStoredGoogleToken()

    // Também verificar se tem provider_token na sessão (logo após o OAuth)
    const { data: { session } } = await supabase.auth.getSession()
    const sessionToken = session?.provider_token
    const sessionRefreshToken = session?.provider_refresh_token

    // Se tem token na sessão mas não no storage, salvar (incluindo refresh token)
    if (sessionToken && !storedToken) {
      saveGoogleToken(sessionToken, sessionRefreshToken || undefined)
    }

    let token = storedToken || sessionToken
    if (!token) {
      return { hasToken: false, apiEnabled: false }
    }

    // Se o token está provavelmente expirado, tentar renovar antes de testar
    if (isTokenExpired() && getStoredGoogleTokens()?.refreshToken) {
      console.log('🔄 Token provavelmente expirado, tentando renovar...')
      const newToken = await refreshGoogleAccessToken()
      if (newToken) {
        token = newToken
      }
    }

    // Verificar se o token ainda é válido fazendo uma requisição simples
    const response = await fetch(`${GOOGLE_CALENDAR_API}/users/me/calendarList?maxResults=1`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 401) {
        // Token expirado — tentar renovar com refresh token
        console.log('🔄 Token expirado (401), tentando renovar...')
        const newToken = await refreshGoogleAccessToken()
        if (newToken) {
          // Verificar se o novo token funciona
          const retryResponse = await fetch(`${GOOGLE_CALENDAR_API}/users/me/calendarList?maxResults=1`, {
            headers: { 'Authorization': `Bearer ${newToken}` }
          })
          if (retryResponse.ok) {
            return { hasToken: true, apiEnabled: true }
          }
        }
        // Refresh falhou — limpar tudo
        clearGoogleToken()
        return { hasToken: false, apiEnabled: false, error: 'Token expirado. Reconecte o Google Calendar.' }
      }
      if (response.status === 403) {
        console.warn('⚠️ Token válido mas Google Calendar API retornou 403.')
        return {
          hasToken: true,
          apiEnabled: false,
          error: 'Google Calendar API não está ativada no Google Cloud Console ou o scope não foi concedido'
        }
      }
      return { hasToken: true, apiEnabled: false, error: `Erro ${response.status}` }
    }

    return { hasToken: true, apiEnabled: true }
  } catch (error) {
    console.error('Erro ao verificar conexão com Google Calendar:', error)
    return { hasToken: false, apiEnabled: false, error: String(error) }
  }
}

/**
 * Obtém o token de acesso do Google (do storage ou da sessão)
 * Tenta renovar automaticamente se estiver expirado
 */
export async function getGoogleAccessToken(): Promise<string | null> {
  try {
    // Se o token está expirado e temos refresh token, renovar primeiro
    if (isTokenExpired() && getStoredGoogleTokens()?.refreshToken) {
      const newToken = await refreshGoogleAccessToken()
      if (newToken) return newToken
    }

    // Tentar do storage local
    const storedToken = getStoredGoogleToken()
    if (storedToken) {
      return storedToken
    }

    // Se não tiver, tentar da sessão
    const { data: { session } } = await supabase.auth.getSession()
    const sessionToken = session?.provider_token
    const sessionRefreshToken = session?.provider_refresh_token

    // Se tem na sessão, salvar para uso futuro
    if (sessionToken) {
      saveGoogleToken(sessionToken, sessionRefreshToken || undefined)
      return sessionToken
    }

    return null
  } catch (error) {
    console.error('Erro ao obter token do Google:', error)
    return null
  }
}

/**
 * Busca tarefas que devem ser sincronizadas
 * Critérios:
 * - data_inicio_tem_hora === true (tem hora definida)
 * - usuário é responsável pela tarefa (via responsavel_id OU tarefas_responsaveis)
 * - status diferente de 'concluido'
 * - ativo = true
 */
export async function getTasksToSync(userId: string): Promise<Tarefa[]> {
  try {
    console.log('🔍 Buscando tarefas para sincronização do usuário:', userId)

    // Abordagem simplificada: buscar diretamente as tarefas elegíveis
    // 1. Tarefas onde o usuário é responsavel_id direto E tem hora definida
    const { data: tarefasDiretas, error: diretasError } = await supabase
      .from('tarefas')
      .select(`
        id,
        nome,
        descricao,
        data_inicio,
        data_inicio_tem_hora,
        data_conclusao,
        status,
        prioridade,
        obra_id,
        obra:obras(nome)
      `)
      .eq('responsavel_id', userId)
      .eq('data_inicio_tem_hora', true)
      .eq('ativo', true)
      .neq('status', 'concluido')
      .not('data_inicio', 'is', null)

    if (diretasError) {
      console.error('❌ Erro ao buscar tarefas diretas:', diretasError)
    }

    console.log('📋 Tarefas com responsavel_id direto e hora definida:', tarefasDiretas?.length || 0)

    // 2. Buscar IDs das tarefas onde o usuário está em tarefas_responsaveis
    const { data: responsaveis, error: respError } = await supabase
      .from('tarefas_responsaveis')
      .select('tarefa_id')
      .eq('usuario_id', userId)

    if (respError) {
      console.error('❌ Erro ao buscar tarefas_responsaveis:', respError)
    }

    // Combinar resultados
    const tarefasMap = new Map<string, Tarefa>()

    // Adicionar tarefas diretas
    if (tarefasDiretas) {
      tarefasDiretas.forEach(t => tarefasMap.set(t.id, toTarefa(t)))
    }

    // Buscar tarefas de tarefas_responsaveis (em lotes pequenos para evitar URL longa)
    if (responsaveis && responsaveis.length > 0) {
      const tarefaIds = responsaveis.map(r => r.tarefa_id)

      // Processar em lotes de 50
      const BATCH_SIZE = 50
      for (let i = 0; i < tarefaIds.length; i += BATCH_SIZE) {
        const batch = tarefaIds.slice(i, i + BATCH_SIZE)

        const { data: tarefasBatch, error: batchError } = await supabase
          .from('tarefas')
          .select(`
            id,
            nome,
            descricao,
            data_inicio,
            data_inicio_tem_hora,
            data_conclusao,
            status,
            prioridade,
            obra_id,
            obra:obras(nome)
          `)
          .in('id', batch)
          .eq('data_inicio_tem_hora', true)
          .eq('ativo', true)
          .neq('status', 'concluido')
          .not('data_inicio', 'is', null)

        if (batchError) {
          console.error(`❌ Erro ao buscar lote ${i / BATCH_SIZE + 1}:`, batchError)
        } else if (tarefasBatch) {
          tarefasBatch.forEach(t => tarefasMap.set(t.id, toTarefa(t)))
        }
      }
    }

    const tarefas = Array.from(tarefasMap.values())
    console.log('✅ Total de tarefas elegíveis para sincronização:', tarefas.length)

    if (tarefas.length > 0) {
      console.log('📝 Tarefas encontradas:', tarefas.map(t => ({
        id: t.id,
        nome: t.nome,
        data_inicio: t.data_inicio
      })))
    } else {
      console.log('⚠️ Nenhuma tarefa elegível encontrada. Critérios:')
      console.log('   - data_inicio_tem_hora = true')
      console.log('   - status != concluido')
      console.log('   - data_inicio não nulo')
      console.log('   - ativo = true')
    }

    return tarefas
  } catch (error) {
    console.error('❌ Erro ao buscar tarefas para sincronização:', error)
    return []
  }
}

/**
 * Converte uma tarefa para formato de evento do Google Calendar
 */
function tarefaToCalendarEvent(tarefa: Tarefa): CalendarEvent {
  const startDate = new Date(tarefa.data_inicio!)

  // Se não tem data de conclusão, usar 1 hora após o início
  let endDate: Date
  if (tarefa.data_conclusao) {
    endDate = new Date(tarefa.data_conclusao)
  } else {
    endDate = new Date(startDate.getTime() + 60 * 60 * 1000) // +1 hora
  }

  // Montar descrição com informações da obra
  const obraNome = (tarefa.obra as any)?.nome || 'Obra não identificada'
  const descricao = [
    tarefa.descricao || '',
    '',
    `Obra: ${obraNome}`,
    `Prioridade: ${tarefa.prioridade}`,
    `Status: ${tarefa.status}`,
    '',
    '---',
    'Sincronizado do ERP ACOR'
  ].join('\n')

  return {
    summary: `[ACOR] ${tarefa.nome}`,
    description: descricao,
    start: {
      dateTime: startDate.toISOString(),
      timeZone: TIME_ZONE
    },
    end: {
      dateTime: endDate.toISOString(),
      timeZone: TIME_ZONE
    },
    extendedProperties: {
      private: {
        acor_tarefa_id: tarefa.id,
        acor_obra_id: tarefa.obra_id
      }
    }
  }
}

/**
 * Cria um evento no Google Calendar
 */
export async function createCalendarEvent(tarefa: Tarefa, _accessToken?: string): Promise<string | null> {
  try {
    const event = tarefaToCalendarEvent(tarefa)

    const response = await fetchWithTokenRefresh(`${GOOGLE_CALENDAR_API}/calendars/primary/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event)
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Erro ao criar evento:', errorData)
      return null
    }

    const createdEvent = await response.json()
    return createdEvent.id
  } catch (error) {
    console.error('Erro ao criar evento no Google Calendar:', error)
    return null
  }
}

/**
 * Busca evento existente pelo ID da tarefa
 */
async function findExistingEvent(tarefaId: string, _accessToken?: string): Promise<string | null> {
  try {
    const response = await fetchWithTokenRefresh(
      `${GOOGLE_CALENDAR_API}/calendars/primary/events?privateExtendedProperty=acor_tarefa_id%3D${tarefaId}`
    )

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    if (data.items && data.items.length > 0) {
      return data.items[0].id
    }

    return null
  } catch (error) {
    console.error('Erro ao buscar evento existente:', error)
    return null
  }
}

/**
 * Atualiza um evento existente no Google Calendar
 */
export async function updateCalendarEvent(
  tarefa: Tarefa,
  eventId: string,
  _accessToken?: string
): Promise<boolean> {
  try {
    const event = tarefaToCalendarEvent(tarefa)

    const response = await fetchWithTokenRefresh(
      `${GOOGLE_CALENDAR_API}/calendars/primary/events/${eventId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event)
      }
    )

    return response.ok
  } catch (error) {
    console.error('Erro ao atualizar evento:', error)
    return false
  }
}

/**
 * Deleta um evento do Google Calendar
 */
export async function deleteCalendarEvent(eventId: string, _accessToken?: string): Promise<boolean> {
  try {
    const response = await fetchWithTokenRefresh(
      `${GOOGLE_CALENDAR_API}/calendars/primary/events/${eventId}`,
      { method: 'DELETE' }
    )

    return response.ok || response.status === 404 // 404 = já deletado
  } catch (error) {
    console.error('Erro ao deletar evento:', error)
    return false
  }
}

/**
 * Sincroniza todas as tarefas elegíveis com o Google Calendar
 */
export async function syncTasksToGoogleCalendar(userId: string): Promise<SyncResult> {
  const result: SyncResult = {
    success: false,
    count: 0,
    errors: [],
    syncedTasks: []
  }

  try {
    // Verificar se tem token (não precisa verificar se API está habilitada - vai dar erro se não estiver)
    const status = await getGoogleCalendarConnectionStatus()
    if (!status.hasToken) {
      result.errors.push('Google Calendar não está conectado. Clique em "Conectar" para autorizar.')
      return result
    }

    // Obter token
    const accessToken = await getGoogleAccessToken()
    if (!accessToken) {
      result.errors.push('Não foi possível obter token de acesso')
      return result
    }

    // Buscar tarefas para sincronizar
    const tarefas = await getTasksToSync(userId)

    if (tarefas.length === 0) {
      result.success = true
      return result
    }

    // Sincronizar cada tarefa
    for (const tarefa of tarefas) {
      try {
        // Verificar se já existe evento para esta tarefa
        const existingEventId = await findExistingEvent(tarefa.id, accessToken)

        if (existingEventId) {
          // Atualizar evento existente
          const updated = await updateCalendarEvent(tarefa, existingEventId, accessToken)
          if (updated) {
            result.count++
            result.syncedTasks.push(tarefa.id)
          } else {
            result.errors.push(`Falha ao atualizar tarefa: ${tarefa.nome}`)
          }
        } else {
          // Criar novo evento
          const eventId = await createCalendarEvent(tarefa, accessToken)
          if (eventId) {
            result.count++
            result.syncedTasks.push(tarefa.id)
          } else {
            result.errors.push(`Falha ao criar evento para tarefa: ${tarefa.nome}`)
          }
        }
      } catch (error) {
        result.errors.push(`Erro na tarefa ${tarefa.nome}: ${error}`)
      }
    }

    result.success = result.errors.length === 0

    // Salvar última sincronização no localStorage
    const syncData = {
      lastSync: new Date().toISOString(),
      count: result.count,
      userId
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(syncData))

    return result
  } catch (error) {
    console.error('Erro na sincronização:', error)
    result.errors.push(`Erro geral: ${error}`)
    return result
  }
}

/**
 * Obtém dados da última sincronização
 */
export function getLastSyncData(): { lastSync: string | null; count: number } {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (data) {
      const parsed = JSON.parse(data)
      return {
        lastSync: parsed.lastSync,
        count: parsed.count || 0
      }
    }
  } catch (error) {
    console.error('Erro ao ler dados de sincronização:', error)
  }
  return { lastSync: null, count: 0 }
}

/**
 * Conecta com Google Calendar solicitando permissões adicionais
 * Usa linkIdentity para adicionar o scope do Calendar sem fazer logout
 */
export async function connectGoogleCalendar(): Promise<{ success: boolean; error?: string }> {
  try {
    // Limpar token antigo se existir
    clearGoogleToken()

    // Usar signInWithOAuth com scopes adicionais
    // O usuário será redirecionado e ao voltar, o provider_token estará disponível
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.readonly',
        redirectTo: window.location.origin + '/ERP-Acor/configuracoes?calendar_connected=true',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent' // Força mostrar a tela de consentimento para obter novos scopes
        }
      }
    })

    if (error) {
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

/**
 * Processa o callback do OAuth e salva os tokens (access + refresh)
 * Deve ser chamado quando a página carrega após o redirect do Google
 */
export async function processOAuthCallback(): Promise<boolean> {
  try {
    // Verificar se estamos voltando do OAuth
    const params = new URLSearchParams(window.location.search)
    const calendarConnected = params.get('calendar_connected')

    if (calendarConnected === 'true') {
      // Buscar a sessão que deve ter o provider_token
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.provider_token) {
        console.log('✅ Token do Google Calendar obtido com sucesso')
        // Salvar access token + refresh token (refresh permite renovar sem re-login)
        saveGoogleToken(session.provider_token, session.provider_refresh_token || undefined)

        if (session.provider_refresh_token) {
          console.log('✅ Refresh token do Google também salvo — renovação automática habilitada')
        } else {
          console.warn('⚠️ Refresh token não recebido — o token expirará em ~1h')
        }

        // Limpar o parâmetro da URL
        const url = new URL(window.location.href)
        url.searchParams.delete('calendar_connected')
        window.history.replaceState({}, '', url.toString())

        return true
      } else {
        console.warn('⚠️ Sessão encontrada mas sem provider_token')
      }
    }

    return false
  } catch (error) {
    console.error('Erro ao processar callback do OAuth:', error)
    return false
  }
}

// ============================================
// Funções de Auto-Sync
// ============================================

const AUTO_SYNC_KEY = 'acor-google-calendar-auto-sync'

/**
 * Verifica se a sincronização automática está ativada
 */
export function isAutoSyncEnabled(): boolean {
  try {
    const value = localStorage.getItem(AUTO_SYNC_KEY)
    // Por padrão, auto-sync está desativado
    return value === 'true'
  } catch {
    return false
  }
}

/**
 * Define se a sincronização automática está ativada
 */
export function setAutoSyncEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(AUTO_SYNC_KEY, enabled ? 'true' : 'false')
  } catch (error) {
    console.error('Erro ao salvar configuração de auto-sync:', error)
  }
}
